import React from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Crumb } from 'components/BreadCrumbs';
import { GenericErrorMessage } from 'components/GenericErrorMessage';
import { ReferralAnswerValidationForm } from 'components/ReferralAnswerValidationForm';
import { ReferralAnswerValidationsList } from 'components/ReferralAnswerValidationsList';
import { ReferralDetailAnswerDisplay } from 'components/ReferralDetailAnswerDisplay';
import { ReferralDetailAnswerForm } from 'components/ReferralDetailAnswerForm';
import { Spinner } from 'components/Spinner';
import { useReferral, useReferralAnswer } from 'data';

const messages = defineMessages({
  crumbEdition: {
    defaultMessage: 'Edition',
    description:
      'Breadcrumb title for the one to the referral draft answer form.',
    id: 'components.ReferralDraftAnswer.crumbEdition',
  },
  crumbValidation: {
    defaultMessage: 'Validation',
    description:
      'Breadcrumb title for the one to the referral draft answer validation form.',
    id: 'components.ReferralDraftAnswer.crumbValidation',
  },
  loadingAnswer: {
    defaultMessage: 'Loading referral answer...',
    description: 'Accessible message for spinner in <ReferralDraftAnswer />.',
    id: 'components.ReferralDraftAnswer.loadingAnswer',
  },
});

interface ReferralDraftAnswerRouteParams {
  answerId: string;
  referralId: string;
}

export const ReferralDraftAnswer: React.FC = () => {
  const { answerId, referralId } = useParams<
    keyof ReferralDraftAnswerRouteParams
  >() as ReferralDraftAnswerRouteParams;

  const { data: referral, status: referralStatus } = useReferral(referralId);
  const { data: answer, status: answerStatus } = useReferralAnswer(answerId);

  const statuses = [referralStatus, answerStatus];

  if (statuses.includes('error')) {
    return <GenericErrorMessage />;
  }

  if (statuses.includes('pending')) {
    return (
      <Spinner>
        <FormattedMessage {...messages.loadingAnswer} />
      </Spinner>
    );
  }

  return (
    <Routes>
      <Route
        path="validation/:validationRequestId"
        element={
          <>
            <ReferralAnswerValidationForm referral={referral!} />
            <ReferralDetailAnswerDisplay
              answer={answer!}
              referral={referral!}
            />
            <Crumb
              key="referral-detail-draft-answers-detail-validation"
              title={<FormattedMessage {...messages.crumbValidation} />}
            />
          </>
        }
      />

      <Route
        path="form"
        element={
          <>
            <ReferralDetailAnswerForm
              answerId={answerId}
              referral={referral!}
            />
            <Crumb
              key="referral-detail-draft-answers-detail-form"
              title={<FormattedMessage {...messages.crumbEdition} />}
            />
          </>
        }
      />

      <Route
        index
        element={
          <>
            <ReferralAnswerValidationsList
              referral={referral!}
              answerId={answerId}
            />
            <ReferralDetailAnswerDisplay
              answer={answer!}
              referral={referral!}
            />
          </>
        }
      />
    </Routes>
  );
};
